/**
 * The keyboard bindings the views pane advertises.
 *
 * The pane lists them, so they have to work. A panel documenting shortcuts that do nothing is worse
 * than no panel: it teaches an operator to reach for something that will not answer, and the second
 * time they try it they stop trusting the rest of the screen too. So this list is deliberately short
 * and grows with the screens that can honor it - `J` arrives with the case list, `R` and `A` with
 * the inspector's actions - rather than being written out in full against a console that can only
 * service two of them.
 *
 * Pure, and tested against plain event-shaped objects rather than a browser, because the decision
 * here is not "did the key go down" but "is this keystroke meant for the page or for the field
 * somebody is typing in" - and that is the part every keyboard-shortcut implementation gets wrong
 * first. A `/` handler that steals the slash out of a device name, or an `R` that resolves a case
 * while somebody is writing the verdict note explaining why, are the same bug.
 */
export type ShortcutAction =
  | 'command-bar'
  | 'help'
  | 'toggle-inspector'
  | 'next-row'
  | 'previous-row'
  | 'resolve'
  | 'acknowledge'
  | 'dismiss'

/**
 * The actions the shell honors itself, on every route, rather than the screen on view.
 *
 * This list is what makes the pane's hint list honest for them. `ViewsPane` filters `SHORTCUT_HINTS`
 * by what the screen registered, and a shell-owned action is registered by nobody - so without this
 * the command bar's own hint would be filtered out, which is why that filter carried a hardcoded
 * exception for it. Naming the set stops that exception growing into a second list nobody notices.
 *
 * Membership here is also the test of whether a binding is honest to advertise. `lib/shortcuts.ts`'s
 * own warning is that a panel documenting a key that does nothing costs the rest of the screen its
 * credibility; a key the shell answers cannot be in that position, because there is no screen that
 * fails to honor it.
 */
export const SHELL_ACTIONS: readonly ShortcutAction[] = ['command-bar', 'help']

/** The shape this needs from a KeyboardEvent, so the test does not need a DOM. */
export interface ShortcutEvent {
  key: string
  metaKey?: boolean
  ctrlKey?: boolean
  altKey?: boolean
  shiftKey?: boolean
  target?: { tagName?: string; isContentEditable?: boolean } | null
}

/**
 * Whether the keystroke belongs to whatever is focused rather than to the page.
 *
 * Exported because the answer is the same everywhere and getting it wrong is silent: a single-letter
 * binding that fires while somebody is typing eats the letter, and the operator sees a field that
 * dropped a character rather than a shortcut that misfired.
 */
export function isTyping(event: ShortcutEvent): boolean {
  const target = event.target
  if (!target) return false
  if (target.isContentEditable) return true

  const tag = (target.tagName ?? '').toUpperCase()
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

/**
 * The action a keystroke means, or null.
 *
 * Escape is the exception that is allowed to fire while typing: it is how somebody gets out of the
 * query bar, and a dismissal that only works when nothing is focused is a dismissal that never works
 * when it is needed.
 */
export function matchShortcut(event: ShortcutEvent): ShortcutAction | null {
  if (event.key === 'Escape') return 'dismiss'

  // The command bar is the one binding that must work from inside another field, because ⌘K is how
  // an operator gets to it from anywhere. A modifier combination cannot be mistaken for typing.
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') return 'command-bar'

  if (event.metaKey || event.ctrlKey || event.altKey) return null
  if (isTyping(event)) return null

  // Shift is not in the bail-out above, so `?` arrives here as `?` on the layouts that need Shift for
  // it and on the ones that do not. `isTyping` has already stopped it firing out of the query bar, so
  // typing a question mark into a search still types a question mark.
  if (event.key === '?') return 'help'

  switch (event.key.toLowerCase()) {
    case 'i':
      return 'toggle-inspector'
    case 'j':
      return 'next-row'
    case 'k':
      return 'previous-row'
    case 'r':
      return 'resolve'
    case 'a':
      return 'acknowledge'
    default:
      return null
  }
}

/**
 * What the views pane prints.
 *
 * `action` is what makes the list honest. The pane used to print this whole array on every route while
 * only `command-bar` was wired, so `I` was advertised on eleven screens that had no inspector. The
 * pane now filters by what the screen on view has registered, and the action is the key it filters on
 * (D-447).
 */
export interface ShortcutHint {
  what: string
  key: string
  action: ShortcutAction
}

export const SHORTCUT_HINTS: readonly ShortcutHint[] = [
  { what: 'Command bar', key: '⌘K', action: 'command-bar' },
  { what: 'Help', key: '?', action: 'help' },
  { what: 'Inspector', key: 'I', action: 'toggle-inspector' }
]

/**
 * The hints worth printing for the screen on view: every key the shell answers itself, and every
 * other key only where the screen registered a handler for it.
 *
 * One function because two surfaces now print this list - the views pane and the keyboard shortcuts
 * dialog under the Help menu - and a key advertised in one and not the other is the dishonesty D-447
 * was written against, just split across two places.
 */
export function advertisedHints(registered: Partial<Record<ShortcutAction, unknown>>): readonly ShortcutHint[] {
  return SHORTCUT_HINTS.filter(
    (hint) => SHELL_ACTIONS.includes(hint.action) || registered[hint.action] !== undefined
  )
}
